import axios from "axios";

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";

// Helper to get auth header
const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
};

// Helper to format Date to ISO string
const toISOString = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toISOString();
};

// Helper to format ISO string to datetime-local string (YYYY-MM-DDTHH:mm)
const toDatetimeLocal = (isoString, original) => {
    if (!isoString) return "";
    // If we have original ISO with timezone, extract the local time from it
    if (original && original.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
        return original.slice(0, 16);
    }
    const date = new Date(isoString);
    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(date.getTime() - offsetMs);
    return localDate.toISOString().slice(0, 16);
};

// Transform frontend event data to backend format
const transformToBackend = (eventData) => {
    return {
        conflictName: eventData.conflictName,
        country: eventData.country,
        typeOfViolence: eventData.typeOfViolence,
        typeOfSource: eventData.typeOfSource,
        exactSource: eventData.exactSources, // Frontend uses 'exactSources'
        description: eventData.description,
        startDate: toISOString(eventData.dateStart), // Frontend uses 'dateStart'
        endDate: toISOString(eventData.dateEnd), // Frontend uses 'dateEnd'
        sideA: eventData.sideA,
        sideB: eventData.sideB,
        sideADeath: eventData.deathA || 0, // Frontend uses 'deathA'
        sideBDeath: eventData.deathB || 0, // Frontend uses 'deathB'
        deathCivilians: eventData.deathCivilians || 0,
        deathUnknown: eventData.deathUnknown || 0,
        status: eventData.status,
        longitude: eventData.longitude,
        latitude: eventData.latitude,
        images: eventData.images || [],
        highImpactIntel: eventData.highImpactIntel || false,
        weapons: eventData.weapons || [],
        markers: eventData.markers || [],
        technicalReportUrl: eventData.technicalReportUrl || "",
        startDateOriginal: eventData.startDateOriginal || "",
        technicalReportUrl: eventData.technicalReportUrl || null,
    };
};

// Transform backend event data to frontend format
const transformToFrontend = (eventData) => {
    return {
        _id: eventData._id,
        conflictName: eventData.conflictName,
        country: eventData.country,
        typeOfViolence: eventData.typeOfViolence,
        typeOfSource: eventData.typeOfSource,
        exactSources: eventData.exactSource, // Backend uses 'exactSource'
        description: eventData.description,
        dateStart: toDatetimeLocal(eventData.startDate, eventData.startDateOriginal), // Backend uses 'startDate'
        dateEnd: toDatetimeLocal(eventData.endDate), // Backend uses 'endDate'
        sideA: eventData.sideA,
        sideB: eventData.sideB,
        deathA: eventData.sideADeath || 0, // Backend uses 'sideADeath'
        deathB: eventData.sideBDeath || 0, // Backend uses 'sideBDeath'
        deathCivilians: eventData.deathCivilians || 0,
        deathUnknown: eventData.deathUnknown || 0,
        status: eventData.status,
        longitude: eventData.longitude,
        latitude: eventData.latitude,
        images: eventData.images || [],
        highImpactIntel: eventData.highImpactIntel || false,
        weapons: eventData.weapons || [],
        markers: eventData.markers || [],
        technicalReportUrl: eventData.technicalReportUrl || "",
    };
};

const eventService = {
    // Get all events with optional filtering and pagination
    getAllEvents: async (filters = {}, page = 1, limit = 100) => {
        try {
            const queryParams = new URLSearchParams();

            // Add pagination params
            queryParams.append("page", page);
            queryParams.append("limit", limit);

            // Add filter params
            if (filters.country) queryParams.append("country", filters.country);
            if (filters.source) queryParams.append("typeOfSource", filters.source);
            if (filters.startDate) queryParams.append("startDate", toISOString(filters.startDate));
            if (filters.endDate) queryParams.append("endDate", toISOString(filters.endDate));
            if (filters.status) queryParams.append("status", filters.status);

            const url = `${API_URL}/events${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
            const response = await axios.get(url, getAuthHeader());

            // Transform all events to frontend format
            if (response.data.status && response.data.data) {
                response.data.data = response.data.data.map(transformToFrontend);
            }

            // Return full response including pagination metadata
            return response.data;
        } catch (error) {
            throw error.response?.data || { errorMessage: "Failed to fetch events" };
        }
    },

    // Get event by ID
    getEventById: async (id) => {
        try {
            const response = await axios.get(`${API_URL}/events/${id}`, getAuthHeader());

            // Transform to frontend format
            if (response.data.status && response.data.data) {
                response.data.data = transformToFrontend(response.data.data);
            }

            return response.data;
        } catch (error) {
            throw error.response?.data || { errorMessage: "Failed to fetch event" };
        }
    },

    // Create new event
    createEvent: async (eventData) => {
        try {
            const backendData = transformToBackend(eventData);
            const response = await axios.post(`${API_URL}/events`, backendData, getAuthHeader());

            // Transform response to frontend format
            if (response.data.status && response.data.data) {
                response.data.data = transformToFrontend(response.data.data);
            }

            return response.data;
        } catch (error) {
            throw error.response?.data || { errorMessage: "Failed to create event" };
        }
    },

    // Update event
    updateEvent: async (id, eventData) => {
        try {
            const backendData = transformToBackend(eventData);
            const response = await axios.put(`${API_URL}/events/${id}`, backendData, getAuthHeader());

            // Transform response to frontend format
            if (response.data.status && response.data.data) {
                response.data.data = transformToFrontend(response.data.data);
            }

            return response.data;
        } catch (error) {
            throw error.response?.data || { errorMessage: "Failed to update event" };
        }
    },

    // Delete event
    deleteEvent: async (id) => {
        try {
            const response = await axios.delete(`${API_URL}/events/${id}`, getAuthHeader());
            return response.data;
        } catch (error) {
            throw error.response?.data || { errorMessage: "Failed to delete event" };
        }
    },

    // Bulk import events (CSV)
    bulkImportEvents: async (eventsArray) => {
        try {
            // Ensure typeOfSource is set to 'CSV Imported' for all events
            const normalizedEvents = eventsArray.map(event => ({
                ...event,
                typeOfSource: 'CSV Imported',
            }));
            // Transform all events to backend format
            const backendEvents = normalizedEvents.map(transformToBackend);

            const response = await axios.post(
                `${API_URL}/events/bulk`,
                { events: backendEvents },
                getAuthHeader()
            );

            return response.data;
        } catch (error) {
            throw error.response?.data || { errorMessage: "Failed to bulk import events" };
        }
    },
};

export default eventService;
